import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, forkJoin, Observable } from 'rxjs';
import { MemberRecord } from '../../../../models/users/dto/member-record';
import { TypeService } from '../../../../services/type/type-service';
import { map, shareReplay, switchMap, take } from 'rxjs/operators';
import { AccountDomainModel } from '../../../../domainModels/account-domain-model';
import { MemberAccountFundAllocation } from '../../../../models/transactions/member-account-fund-allocation';
import { DropDownItem } from '../../../../models/shared/stylesheet/drop-down-item';

@Injectable()
export class ChangeInvestmentInstructionsSubmissionFormViewModel {
  private typeService = inject(TypeService);
  private accountDomainModel = inject(AccountDomainModel);

  private _user = new BehaviorSubject<MemberRecord | null>(null);
  public user$ = this._user as Observable<MemberRecord | null>;
  public setUser(user: MemberRecord): void {
    this._user.next(user);
  }

  private accountCanTransferFunds(allocations: MemberAccountFundAllocation[]): boolean {
    if (!allocations.length) return false;

    const hasAccountBalance = allocations.some(fund => fund.totalBalance > 0);
    const totalBalance = allocations.reduce((sum, fund) => sum + fund.totalBalance, 0);
    const cashFund = allocations.find(fund => fund.isCashFund());

    return hasAccountBalance && cashFund?.totalBalance !== totalBalance;
  }

  public accountTypeDropdowns$ = combineLatest([this.user$, this.typeService.accountTypes$]).pipe(
    take(1),
    switchMap(([user, types]) => {
      const accountTypes = types.filter(type => type?.categoryId === 3);
      const requests = accountTypes.map(type =>
        this.accountDomainModel.ipGetMemberAccountBalancesByFund(user?.id ?? -1, type.id).pipe(
          map(balances => {
            return {
              type,
              canTransferFunds: this.accountCanTransferFunds(balances)
            };
          })
        )
      );
      return forkJoin(requests);
    }),
    map(results => {
      return results.map(r => new DropDownItem(r.type.name + 's', r.type.id, !r.canTransferFunds));
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );
}
